import type { AxiosInstance } from "axios";

export type PointBounds = [number, number, number, number];

export interface ApiMapPoint {
  id: number;
  attributes: Record<string, any>;
}

interface PointPage {
  data: ApiMapPoint[];
  meta: { pagination: { page: number; pageCount: number; total: number } };
}

export class MapPointQuery {
  constructor(private readonly client: AxiosInstance, private readonly path: string) {}

  async getPoints(categoryId: number, locale: string, bounds: PointBounds, signal: AbortSignal): Promise<{ data: ApiMapPoint[] }> {
    const [west, south, east, north] = bounds;
    if (!bounds.every(Number.isFinite) || west > east || south > north) {
      throw new Error("Map bounds must be finite and ordered west, south, east, north.");
    }
    const params: Record<string, string | number> = {
      "filters[ip_category][id][$eq]": categoryId,
      "filters[latitudine][$gte]": south,
      "filters[latitudine][$lte]": north,
      "filters[longitudine][$gte]": west,
      "filters[longitudine][$lte]": east,
      "pagination[pageSize]": 250,
      "sort[0]": "id:asc",
      locale,
    };
    if (categoryId === 5) {
      params["populate[ip_category]"] = "*";
      params["populate[civil_shelter][populate][judet_id][fields][0]"] = "nume";
      params["populate[civil_shelter][populate][judet_id][fields][1]"] = "abreviere";
    } else {
      params.populate = "*";
    }

    const points: ApiMapPoint[] = [];
    const ids = new Set<number>();
    let total: number | undefined;
    let pageCount = 1;
    for (let page = 1; page <= pageCount; page += 1) {
      if (signal.aborted) throw new DOMException("Map request canceled", "AbortError");
      const { data: response } = await this.client.get<PointPage>(this.path, {
        params: { ...params, "pagination[page]": page }, signal,
      });
      const pagination = response.meta?.pagination;
      if (!Array.isArray(response.data) || !pagination || pagination.page !== page ||
          !Number.isInteger(pagination.pageCount) || pagination.pageCount < 0 ||
          !Number.isInteger(pagination.total) || pagination.total < 0) {
        throw new Error("Map points API returned invalid pagination.");
      }
      if (total !== undefined && (total !== pagination.total || pageCount !== pagination.pageCount)) {
        throw new Error("Map points changed during pagination; reload this area.");
      }
      total = pagination.total;
      pageCount = pagination.pageCount;
      for (const point of response.data) {
        if (ids.has(point.id)) throw new Error("Map points API returned duplicate identifiers.");
        ids.add(point.id);
        points.push(point);
      }
    }
    if (points.length !== total) throw new Error("Map points API returned an incomplete area.");
    return { data: points };
  }
}
